// Shared rules for "which directs need billing, and when."
//
// Both the owner Dashboard summary and the Ready-to-bill screen import
// from here so the two surfaces can never drift apart (before this, each
// computed readiness on its own and the dashboard quietly disagreed with
// the billing screen — DAI was invisible and date-passed Orion directs
// were missed).

// Broker-name hints, mirrored from Ready-to-bill's tab routing.
//   DAI   → flat-rate, forwarded as a whole bundle (one schedule email).
//   Orion → directs that bill hourly per job (LALI / EFFY / Iberjoya /
//           MWI / Orion itself).
const DAI_HINTS = ["dai", "dana augustine", "augustine"];
const ORION_HINTS = ["lali", "effy", "iberjoya", "mwi", "orion"];

// Is this a DAI-style flat-rate bundled job? Matches either the explicit
// billingType flag or the broker name, same as Ready-to-bill's "bundled"
// tab.
export function isDaiJob(job) {
  if ((job.billingType || "").toLowerCase() === "bundled") return true;
  const bn = (job.brokerName || "").toLowerCase();
  return DAI_HINTS.some((h) => bn.includes(h));
}

// Is this an Orion direct (non-DAI)? DAI is excluded so a job is never
// both at once.
export function isOrionDirectJob(job) {
  if (isDaiJob(job)) return false;
  const bn = (job.brokerName || "").toLowerCase();
  return ORION_HINTS.some((h) => bn.includes(h));
}

// Does this job bill by DATE rather than by guard completion? All
// point-to-point directs do, regardless of broker (Orion brokers,
// Malca, Kallati/Executive, Ferrari…) — the owner bills off the
// schedule, so once the job's date arrives it needs billing even if a
// guard never tapped "complete". The Orion-hint brokers keep their
// historical any-job-type behavior; DAI stays excluded (it's billed as
// forwarded bundles, not per job).
export function directBillsByDate(job) {
  if (isDaiJob(job)) return false;
  if (isOrionDirectJob(job)) return true;
  return String(job.jobType || "").toLowerCase() === "direct";
}

// A single job is "ready to bill" once a guard marked it complete OR its
// scheduled date is today-or-earlier. We use today-or-earlier (<= 0) on
// purpose so a job dated today counts as ready ON that day, not the day
// after. Undated jobs are never ready — the owner must set a date first.
export function jobReadyByDate(job, today) {
  if (job.status === "complete") return true;
  return !!job.scheduledDate && job.scheduledDate.localeCompare(today) <= 0;
}

// Should this job be auto-closed (marked complete) so it drops off the
// ACTIVE Job inbox? Jobs that bill by DATE rather than by guard completion
// are rarely worked through the guard app, so nothing ever advances them —
// they sit "In pool" indefinitely while also showing on Ready to bill,
// which is where they actually get dealt with. DAI (forwarded batches) and
// every other direct behave the same way here, so the rule covers both.
//
// Rule: bills-by-date, still in a pre-work status, and its date has STRICTLY
// passed — never on the day, the run may still be underway. A job a guard is
// actively working (inProgress) is left alone, as is anything already
// complete or cancelled. Batch readiness and forwarding are untouched.
export function shouldAutoClosePastJob(job, today) {
  if (!job || !job.scheduledDate || !today) return false;
  if (!["intake", "pool", "grabbed", "confirmed"].includes(job.status)) return false;
  if (!isDaiJob(job) && !directBillsByDate(job)) return false;
  return String(job.scheduledDate).localeCompare(today) < 0;
}

// Today as a YYYY-MM-DD string, matching how scheduledDate is stored.
// Pure string compare downstream — no Date math or timezone to reason
// about.
export function todayYmd() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
